import { Stack } from "@biblioteksentralen/react";
import { ComponentProps, MutableRefObject } from "react";
import { DefaultContainer } from "../../../../components/ContentContainer";
import { WorkCoverImage } from "../../WorkCoverImage";
import { MovieReservation } from "../../reservation/MovieReservation";
import { Reservation } from "../../reservation/Reservation";
import { reservationFullWidthBreakpoint } from "../../reservation/ReservationContainer";
import { ReservationTextContainer } from "../../reservation/ReservationTextContainer";
import { WorkInfo } from "./WorkInfo";
import { useRediaPlatformContext } from "../../../../rediaPlatform/RediaPlatformProvider";
import { useWorkViewContext } from "../WorkViewContextProvider";

type WorkViewBannerProps = {
  manifestationsTitleRef: MutableRefObject<HTMLAnchorElement | null>;
};

const reservationLayoutProps = {
  flexGrow: "1",
  minWidth: { base: "100%", [reservationFullWidthBreakpoint]: "16rem" },
  borderRadius: { base: "0", [reservationFullWidthBreakpoint]: "md" },
  height: "100%",
} satisfies Partial<ComponentProps<typeof Reservation>>;

export const WorkViewBanner = ({ manifestationsTitleRef }: WorkViewBannerProps) => {
  const { rediaPlatformIsEnabled } = useRediaPlatformContext();
  const { work, holdingsData, hasValidHoldings, currentRepresentativeWorkTriple, currentVariant } =
    useWorkViewContext();
  const coverImageQuality = !rediaPlatformIsEnabled || holdingsData?.holdings ? "original" : "blur";
  const hasHoldingsDataError = holdingsData?.hasError;

  return (
    <DefaultContainer withoutPaddingXBelow="lg">
      <Stack
        flexDirection={{ base: "column", [reservationFullWidthBreakpoint]: "row" }}
        alignItems="flex-start"
        justifyContent="flex-start"
        width="100%"
      >
        <Stack
          flexDirection={{ base: "column", sm: "row" }}
          alignItems={{ base: "center", sm: "flex-start" }}
          justifyContent="flex-start"
          flexGrow="1"
          position="relative"
          width="100%"
          spacing="2rem"
          padding={{ base: "1rem", sm: "1.5rem", lg: 0 }}
        >
          {work?.getRawBmdbWork() && (
            <WorkCoverImage
              work={work?.getRawBmdbWork()}
              representativeManifestation={currentRepresentativeWorkTriple?.getSortableManifestation()}
              quality={coverImageQuality}
              width={{ base: "8rem", md: "10rem", lg: "12rem" }}
              flexShrink={0}
              showFavoriteButton
            />
          )}
          <WorkInfo manifestationsTitleRef={manifestationsTitleRef} flexGrow="1" alignSelf="stretch" />
        </Stack>
        {hasHoldingsDataError && (
          <ReservationTextContainer
            getText={(t) => t("Noe gikk galt ved henting av beholdningsstatus.")}
            currentDocumentType={currentVariant?.variant.documentType}
            {...reservationLayoutProps}
          />
        )}
        {rediaPlatformIsEnabled && !hasHoldingsDataError && !hasValidHoldings && (
          <ReservationTextContainer
            getText={(t, name) => `${name} ${t("er for tiden ikke tilgjengelig")}.`}
            currentDocumentType={currentVariant?.variant.documentType}
            {...reservationLayoutProps}
          />
        )}
        {(!rediaPlatformIsEnabled || (!hasHoldingsDataError && hasValidHoldings)) && work?.isMovie && (
          <MovieReservation {...reservationLayoutProps} />
        )}
        {(!rediaPlatformIsEnabled || (!hasHoldingsDataError && hasValidHoldings)) && !work?.isMovie && (
          <Reservation {...reservationLayoutProps} />
        )}
      </Stack>
    </DefaultContainer>
  );
};
